import { useEffect, useRef } from 'react'

import { type Edge, useReactFlow } from '@xyflow/react'

import { buildParentMap, getAncestors } from './treeUtils'
import { type CustomNode } from './types'

interface UseNodeSelectionFromRouteProps {
  nodeId?: string
  setNodes: React.Dispatch<React.SetStateAction<CustomNode[]>>
  edges: Edge[]
}

const useNodeSelectionFromRoute = ({ nodeId, setNodes, edges }: UseNodeSelectionFromRouteProps) => {
  const { fitView } = useReactFlow()

  // Build parent map from original edges
  const parentMapRef = useRef<Map<string, string>>(new Map())
  useEffect(() => {
    parentMapRef.current = buildParentMap(edges)
  }, [edges])

  useEffect(() => {
    if (nodeId) {
      setNodes((nds) => {
        const nodeToSelect = nds.find((n) => n.id === nodeId || n.data.node_uuid === nodeId)
        if (!nodeToSelect) {
          return nds
        }

        const targetNodeId = nodeToSelect.id
        const ancestorsToExpand = new Set(getAncestors(targetNodeId, parentMapRef.current))

        fitView({
          duration: 300,
          nodes: [nodeToSelect],
          maxZoom: 1,
          padding: { right: '70%', bottom: '60%' },
        })

        // Expand all ancestors and update selection in a single pass
        return nds.map((n) => {
          const isAncestorToExpand = ancestorsToExpand.has(n.id) && !n.data.expanded
          const isNodeToSelect = n.id === targetNodeId && !n.selected
          const isNodeToDeselect = n.id !== targetNodeId && n.selected

          if (isAncestorToExpand) {
            return {
              ...n,
              data: { ...n.data, expanded: true },
            }
          }
          if (isNodeToSelect) {
            return { ...n, selected: true }
          }
          if (isNodeToDeselect) {
            return { ...n, selected: false }
          }
          return n
        })
      })
    } else {
      // Deselect all nodes when there's no id param
      setNodes((nds) => {
        const hasSelectedNodes = nds.some((n) => n.selected)
        if (!hasSelectedNodes) {
          return nds
        }
        return nds.map((n) => (n.selected ? { ...n, selected: false } : n))
      })
    }
  }, [nodeId, setNodes, fitView])
}

export default useNodeSelectionFromRoute
